import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'
import {
  decodeToken,
  encodeToken,
  formatJson,
  generateExampleKeyPair,
  jsonMarkup,
  tokenPartMarkup,
  verifyToken,
  type DecodedToken,
  type JsonObject,
} from './lib/jwt'

type Mode = 'decoder' | 'encoder'
type Theme = 'system' | 'dark' | 'light'
type StatusTone = 'success' | 'error' | 'neutral'

const DEFAULT_SECRET = 'a-string-secret-at-least-256-bits-long'
const DEFAULT_TOKEN =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiYWRtaW4iOnRydWUsImlhdCI6MTUxNjIzOTAyMn0.KMUFsIDTnFmyG3nMiGM6H9FNFUROf3wh7SmqJp-QV30'
const DEFAULT_HEADER = formatJson({ alg: 'HS256', typ: 'JWT' })
const DEFAULT_PAYLOAD = formatJson({
  sub: '1234567890',
  name: 'John Doe',
  admin: true,
  iat: 1516239022,
})

const HEADER_CLAIMS: Record<string, string> = {
  alg: 'Algorithm used to sign or secure the token',
  typ: 'Media type of the complete token',
  cty: 'Content type of the secured payload',
  kid: 'Identifier for the key used to secure the token',
  jku: 'URL for a JSON Web Key Set',
  x5t: 'X.509 certificate SHA-1 thumbprint',
}

const PAYLOAD_CLAIMS: Record<string, string> = {
  iss: 'Issuer that created the token',
  sub: 'Subject represented by the token',
  aud: 'Audience the token is intended for',
  exp: 'Expiration time after which the token is invalid',
  nbf: 'Time before which the token is invalid',
  iat: 'Time at which the token was issued',
  jti: 'Unique identifier for the token',
}

function parseObject(value: string):
  | { value: JsonObject; error?: never }
  | { value?: never; error: string } {
  try {
    const parsed = JSON.parse(value) as unknown
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return { error: 'Value must be a JSON object' }
    }
    return { value: parsed as JsonObject }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Invalid JSON',
    }
  }
}

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Unexpected error'
  return error.message.replace(/^.*?failed:\s*/i, '')
}

function keyKind(algorithm: unknown): 'none' | 'secret' | 'asymmetric' {
  if (algorithm === 'none') return 'none'
  if (typeof algorithm === 'string' && algorithm.startsWith('HS')) {
    return 'secret'
  }
  return 'asymmetric'
}

function Icon({ name }: { name: 'copy' | 'clear' | 'expand' }) {
  if (name === 'copy') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <rect x="5" y="5" width="9" height="9" rx="2" />
        <path d="M11 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h1" />
      </svg>
    )
  }

  if (name === 'expand') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M9 2h5v5M14 2 9 7M7 14H2V9M2 14l5-5" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="m6 5 5 5M11 5l-5 5" />
      <path d="M6 2h7a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H6L1 8z" />
    </svg>
  )
}

function ThemePicker({ theme, onChange }: { theme: Theme; onChange: (theme: Theme) => void }) {
  return (
    <div className="theme-picker" role="group" aria-label="Theme">
      <button
        className={`theme-button ${theme === 'system' ? 'is-active' : ''}`}
        onClick={() => onChange('system')}
        aria-label="Use system theme"
        title="System theme"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="1" y="2" width="14" height="11" rx="2" />
          <path d="M5 15h6M8 13v2" />
        </svg>
      </button>
      <button
        className={`theme-button ${theme === 'dark' ? 'is-active' : ''}`}
        onClick={() => onChange('dark')}
        aria-label="Use dark theme"
        title="Dark theme"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M14.3 10.1A6.8 6.8 0 0 1 5.4 1.4a6.8 6.8 0 1 0 8.9 8.7Z" />
        </svg>
      </button>
      <button
        className={`theme-button ${theme === 'light' ? 'is-active' : ''}`}
        onClick={() => onChange('light')}
        aria-label="Use light theme"
        title="Light theme"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="3" />
          <path d="M8 1V0M8 16v-1M1 8H0M16 8h-1M3 3 2 2M14 14l-1-1M13 3l1-1M2 14l1-1" />
        </svg>
      </button>
    </div>
  )
}

function Status({ tone = 'success', children }: { tone?: StatusTone; children: string }) {
  return (
    <p className={`validation-status ${tone}`} role="status">
      {children}
    </p>
  )
}

interface HighlightedTextareaProps {
  value: string
  onChange: (value: string) => void
  markup: string
  ariaLabel: string
  className: string
  inputRef?: RefObject<HTMLTextAreaElement | null>
}

function HighlightedTextarea({
  value,
  onChange,
  markup,
  ariaLabel,
  className,
  inputRef,
}: HighlightedTextareaProps) {
  return (
    <div className={`code-input ${className}`}>
      <pre
        className="code-highlight"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: `${markup}${value.endsWith('\n') ? ' ' : ''}` }}
      />
      <textarea
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
        aria-label={ariaLabel}
      />
    </div>
  )
}

function CopyButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="icon-button" onClick={onClick} aria-label={label} title="Copy">
      <Icon name="copy" />
    </button>
  )
}

function ClaimsView({ value, kind }: { value: JsonObject; kind: 'header' | 'payload' }) {
  const descriptions = kind === 'header' ? HEADER_CLAIMS : PAYLOAD_CLAIMS
  const entries = Object.entries(value)

  if (entries.length === 0) {
    return <p className="empty-claims">No claims found.</p>
  }

  return (
    <div className="claims-view">
      {entries.map(([name, claim]) => {
        const isTimestamp = ['exp', 'nbf', 'iat'].includes(name) && typeof claim === 'number'
        const rendered = typeof claim === 'string' ? claim : JSON.stringify(claim)
        return (
          <div className="claim-row" key={name}>
            <span className="claim-name">{name}</span>
            <span className="claim-value">
              {rendered}
              {isTimestamp && (
                <span className="claim-description">
                  {new Date(claim * 1000).toLocaleString()}
                </span>
              )}
              {descriptions[name] && (
                <span className="claim-description">{descriptions[name]}</span>
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default function App() {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('jwt-debugger-theme')
    return saved === 'dark' || saved === 'light' || saved === 'system'
      ? saved
      : 'system'
  })
  const [mode, setMode] = useState<Mode>('decoder')
  const [decoderToken, setDecoderToken] = useState(DEFAULT_TOKEN)
  const [verificationValue, setVerificationValue] = useState(DEFAULT_SECRET)
  const [verificationEncoded, setVerificationEncoded] = useState(false)
  const [signatureStatus, setSignatureStatus] = useState<{
    tone: StatusTone
    message: string
  }>({ tone: 'neutral', message: 'Signature not verified' })
  const [autoFocus, setAutoFocus] = useState(false)
  const [headerView, setHeaderView] = useState<'json' | 'claims'>('json')
  const [payloadView, setPayloadView] = useState<'json' | 'claims'>('json')
  const [expandedCard, setExpandedCard] = useState<'header' | 'payload' | null>(null)
  const [headerText, setHeaderText] = useState(DEFAULT_HEADER)
  const [payloadText, setPayloadText] = useState(DEFAULT_PAYLOAD)
  const [signingValue, setSigningValue] = useState(DEFAULT_SECRET)
  const [signingEncoded, setSigningEncoded] = useState(false)
  const [encodedOutput, setEncodedOutput] = useState(DEFAULT_TOKEN)
  const [encodingError, setEncodingError] = useState('')
  const [selectedExample, setSelectedExample] = useState('')
  const [isGeneratingExample, setIsGeneratingExample] = useState(false)
  const [toast, setToast] = useState('')
  const tokenInputRef = useRef<HTMLTextAreaElement>(null)

  const decoded = useMemo<
    { value: DecodedToken; error?: never } | { value?: never; error: string }
  >(() => {
    try {
      return { value: decodeToken(decoderToken) }
    } catch (error) {
      return { error: errorMessage(error) }
    }
  }, [decoderToken])

  const parsedHeader = useMemo(() => parseObject(headerText), [headerText])
  const parsedPayload = useMemo(() => parseObject(payloadText), [payloadText])
  const encoderAlgorithm = parsedHeader.value?.alg
  const encoderKeyKind = keyKind(encoderAlgorithm)
  const decoderKeyKind = keyKind(decoded.value?.header.alg)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('jwt-debugger-theme', theme)
  }, [theme])

  useEffect(() => {
    if (autoFocus) tokenInputRef.current?.focus()
  }, [autoFocus])

  useEffect(() => {
    let current = true

    const verify = async () => {
      if (decoded.error) {
        setSignatureStatus({ tone: 'neutral', message: 'Signature not verified' })
        return
      }

      if (decoderKeyKind !== 'none' && !verificationValue.trim()) {
        setSignatureStatus({ tone: 'neutral', message: 'Signature not verified' })
        return
      }

      setSignatureStatus({ tone: 'neutral', message: 'Verifying signature...' })
      try {
        await verifyToken(decoderToken, verificationValue, verificationEncoded)
        if (current) setSignatureStatus({ tone: 'success', message: 'Signature verified' })
      } catch (error) {
        if (current) {
          setSignatureStatus({ tone: 'error', message: errorMessage(error) })
        }
      }
    }

    void verify()
    return () => {
      current = false
    }
  }, [decoded.error, decoderKeyKind, decoderToken, verificationEncoded, verificationValue])

  useEffect(() => {
    let current = true

    const encode = async () => {
      if (!parsedHeader.value || !parsedPayload.value) {
        setEncodedOutput('')
        setEncodingError(parsedHeader.error ?? parsedPayload.error ?? 'Invalid JSON')
        return
      }

      if (encoderKeyKind !== 'none' && !signingValue.trim()) {
        setEncodedOutput('')
        setEncodingError('Enter a signing key')
        return
      }

      try {
        const token = await encodeToken(
          parsedHeader.value,
          parsedPayload.value,
          signingValue,
          signingEncoded,
        )
        if (current) {
          setEncodedOutput(token)
          setEncodingError('')
        }
      } catch (error) {
        if (current) {
          setEncodedOutput('')
          setEncodingError(errorMessage(error))
        }
      }
    }

    void encode()
    return () => {
      current = false
    }
  }, [encoderKeyKind, parsedHeader, parsedPayload, signingEncoded, signingValue])

  useEffect(() => {
    if (!toast) return
    const timeout = window.setTimeout(() => setToast(''), 1600)
    return () => window.clearTimeout(timeout)
  }, [toast])

  const copy = async (value: string, label = 'Copied') => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.append(textarea)
      textarea.select()
      document.execCommand('copy')
      textarea.remove()
    }
    setToast(label)
  }

  const changeMode = (nextMode: Mode) => {
    if (nextMode === mode) return

    if (nextMode === 'encoder' && decoded.value) {
      setHeaderText(formatJson(decoded.value.header))
      setPayloadText(formatJson(decoded.value.payload))
      if (decoderKeyKind === 'secret') setSigningValue(verificationValue)
    }

    if (nextMode === 'decoder' && encodedOutput) {
      setDecoderToken(encodedOutput)
      if (encoderKeyKind === 'secret') setVerificationValue(signingValue)
    }

    setMode(nextMode)
  }

  const generateExample = async (selection: string) => {
    if (!selection) return
    setSelectedExample(selection)
    setIsGeneratingExample(true)

    try {
      const payload = {
        sub: '1234567890',
        name: 'John Doe',
        admin: true,
        iat: Math.floor(Date.now() / 1000),
      }
      let algorithm = selection
      let privateKey = selection === 'none' ? '' : DEFAULT_SECRET
      let publicKey = privateKey

      if (!selection.startsWith('HS') && selection !== 'none') {
        const keys = await generateExampleKeyPair(selection)
        algorithm = keys.algorithm
        privateKey = keys.privateKey
        publicKey = keys.publicKey
      }

      const header = { alg: algorithm, typ: 'JWT' }
      const token = await encodeToken(header, payload, privateKey)

      setHeaderText(formatJson(header))
      setPayloadText(formatJson(payload))
      setSigningValue(privateKey)
      setVerificationValue(publicKey)
      setSigningEncoded(false)
      setVerificationEncoded(false)
      setEncodedOutput(token)
      setDecoderToken(token)
      setToast(`${selection} example generated locally`)
    } catch (error) {
      setToast(`Could not generate ${selection}: ${errorMessage(error)}`)
    } finally {
      setIsGeneratingExample(false)
    }
  }

  const decoderHeader = (decoded.value?.header ?? {}) as JsonObject
  const decoderPayload = (decoded.value?.payload ?? {}) as JsonObject
  const decoderHeaderText = decoded.value ? formatJson(decoderHeader) : ''
  const decoderPayloadText = decoded.value ? formatJson(decoderPayload) : ''
  const asymmetricDecoder = decoderKeyKind === 'asymmetric'
  const asymmetricEncoder = encoderKeyKind === 'asymmetric'

  return (
    <>
      <header className="site-header shell" aria-label="Application header">
        <div className="brand" aria-label="JWT Local Debugger">
          <span className="local-token-mark" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <h1 className="brand-title">JWT Debugger</h1>
        </div>
        <div className="privacy-note">
          <span className="privacy-dot" aria-hidden="true" />
          Everything stays in this browser
        </div>
        <ThemePicker theme={theme} onChange={setTheme} />
      </header>

      <main>
        <section className="debugger shell" aria-label="JWT debugger">
          <div className="mode-tabs" role="tablist" aria-label="Debugger mode">
            <button
              id="decoder-tab"
              className={`mode-tab ${mode === 'decoder' ? 'is-active' : ''}`}
              role="tab"
              aria-selected={mode === 'decoder'}
              aria-controls="decoder-panel"
              onClick={() => changeMode('decoder')}
            >
              JWT Decoder
            </button>
            <button
              id="encoder-tab"
              className={`mode-tab ${mode === 'encoder' ? 'is-active' : ''}`}
              role="tab"
              aria-selected={mode === 'encoder'}
              aria-controls="encoder-panel"
              onClick={() => changeMode('encoder')}
            >
              JWT Encoder
            </button>
          </div>

          <div className="debugger-intro">
            <p>
              {mode === 'decoder'
                ? "Paste a JWT below that you'd like to decode, validate, and verify."
                : 'Fill in the fields below to generate a signed JWT.'}
            </p>
            <label className="example-picker">
              <span>{isGeneratingExample ? 'Generating locally...' : 'Generate example'}</span>
              <select
                value={selectedExample}
                disabled={isGeneratingExample}
                onChange={(event) => void generateExample(event.target.value)}
                aria-label="Generate a JWT example"
              >
                <option value="">Select signing algorithm</option>
                <option value="none">none</option>
                <option value="HS256">HS256</option>
                <option value="HS384">HS384</option>
                <option value="HS512">HS512</option>
                <option value="RS256">RS256</option>
                <option value="RS384">RS384</option>
                <option value="RS512">RS512</option>
                <option value="ES256">ES256</option>
                <option value="ES384">ES384</option>
                <option value="ES512">ES512</option>
                <option value="PS256">PS256</option>
                <option value="PS384">PS384</option>
                <option value="PS512">PS512</option>
                <option value="Ed25519">EdDSA (Ed25519)</option>
                <option value="Ed448">EdDSA (Ed448)</option>
              </select>
            </label>
          </div>

          <section
            id="decoder-panel"
            className="mode-panel"
            role="tabpanel"
            aria-labelledby="decoder-tab"
            hidden={mode !== 'decoder'}
          >
            <div className="decoder-grid">
              <div className="decoder-input-column">
                <div className="section-title-row">
                  <h2>Encoded Token</h2>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoFocus}
                      onChange={(event) => setAutoFocus(event.target.checked)}
                    />
                    <span>Enable auto-focus</span>
                  </label>
                </div>

                <article className="editor-card token-card tall-card">
                  <header className="card-header">
                    <span className="card-title">
                      <span className="terminal-icon">›_</span> JSON Web Token (JWT)
                    </span>
                    <span className="card-actions">
                      <CopyButton
                        label="Copy encoded token"
                        onClick={() => void copy(decoderToken, 'Token copied')}
                      />
                      <button
                        className="icon-button"
                        onClick={() => setDecoderToken('')}
                        aria-label="Clear encoded token"
                        title="Clear"
                      >
                        <Icon name="clear" />
                      </button>
                    </span>
                  </header>
                  <HighlightedTextarea
                    inputRef={tokenInputRef}
                    value={decoderToken}
                    onChange={setDecoderToken}
                    markup={tokenPartMarkup(decoderToken)}
                    className="token-input"
                    ariaLabel="JWT editor"
                  />
                </article>
                <Status tone={decoded.error ? 'error' : 'success'}>
                  {decoded.error ?? 'Valid JWT'}
                </Status>
              </div>

              <div className="decoder-output-column">
                <section className="decoded-section">
                  <h2>Decoded Header</h2>
                  <article
                    className={`editor-card decoded-card ${expandedCard === 'header' ? 'expanded' : ''}`}
                  >
                    <header className="card-header tabbed-header">
                      <div className="card-tabs" role="tablist" aria-label="Decoded header view">
                        <button
                          className={`card-tab ${headerView === 'json' ? 'is-active' : ''}`}
                          onClick={() => setHeaderView('json')}
                        >
                          JSON
                        </button>
                        <button
                          className={`card-tab ${headerView === 'claims' ? 'is-active' : ''}`}
                          onClick={() => setHeaderView('claims')}
                        >
                          Claims Breakdown
                        </button>
                      </div>
                      <span className="card-actions">
                        <CopyButton
                          label="Copy decoded header"
                          onClick={() => void copy(decoderHeaderText, 'Header copied')}
                        />
                        <button
                          className="icon-button"
                          onClick={() => setExpandedCard(expandedCard === 'header' ? null : 'header')}
                          aria-label="Expand decoded header"
                          title="Expand"
                        >
                          <Icon name="expand" />
                        </button>
                      </span>
                    </header>
                    <div className="decoded-body">
                      {headerView === 'json' ? (
                        <pre
                          className="json-output"
                          dangerouslySetInnerHTML={{ __html: jsonMarkup(decoderHeaderText) }}
                        />
                      ) : (
                        <ClaimsView value={decoderHeader} kind="header" />
                      )}
                    </div>
                  </article>
                </section>

                <section className="decoded-section">
                  <h2>Decoded Payload</h2>
                  <article
                    className={`editor-card decoded-card payload-card ${expandedCard === 'payload' ? 'expanded' : ''}`}
                  >
                    <header className="card-header tabbed-header">
                      <div className="card-tabs" role="tablist" aria-label="Decoded payload view">
                        <button
                          className={`card-tab ${payloadView === 'json' ? 'is-active' : ''}`}
                          onClick={() => setPayloadView('json')}
                        >
                          JSON
                        </button>
                        <button
                          className={`card-tab ${payloadView === 'claims' ? 'is-active' : ''}`}
                          onClick={() => setPayloadView('claims')}
                        >
                          Claims Breakdown
                        </button>
                      </div>
                      <span className="card-actions">
                        <CopyButton
                          label="Copy decoded payload"
                          onClick={() => void copy(decoderPayloadText, 'Payload copied')}
                        />
                        <button
                          className="icon-button"
                          onClick={() => setExpandedCard(expandedCard === 'payload' ? null : 'payload')}
                          aria-label="Expand decoded payload"
                          title="Expand"
                        >
                          <Icon name="expand" />
                        </button>
                      </span>
                    </header>
                    <div className="decoded-body payload-body">
                      {payloadView === 'json' ? (
                        <pre
                          className="json-output"
                          dangerouslySetInnerHTML={{ __html: jsonMarkup(decoderPayloadText) }}
                        />
                      ) : (
                        <ClaimsView value={decoderPayload} kind="payload" />
                      )}
                    </div>
                  </article>
                </section>

                <section className="verification-section">
                  <div className="section-title-row verification-title">
                    <div>
                      <h2>
                        JWT Signature Verification <span>(Optional)</span>
                      </h2>
                      <p>
                        {asymmetricDecoder
                          ? 'Enter the public key used to sign the JWT below:'
                          : decoderKeyKind === 'none'
                            ? 'This unsecured JWT must contain an empty signature.'
                            : 'Enter the secret used to sign the JWT below:'}
                      </p>
                    </div>
                    {decoderKeyKind === 'secret' && (
                      <label className="switch-label">
                        <span>Base64url encoded</span>
                        <input
                          type="checkbox"
                          checked={verificationEncoded}
                          onChange={(event) => setVerificationEncoded(event.target.checked)}
                        />
                        <span className="switch" aria-hidden="true" />
                      </label>
                    )}
                  </div>
                  {decoderKeyKind !== 'none' && (
                    <article className="editor-card verification-card">
                      <header className="card-header">
                        <span className="card-title">
                          <span className="terminal-icon">›_</span>{' '}
                          {asymmetricDecoder ? 'Public Key' : 'Secret'}
                        </span>
                        <span className="card-actions">
                          <CopyButton
                            label="Copy verification key"
                            onClick={() => void copy(verificationValue, 'Key copied')}
                          />
                          <button
                            className="icon-button"
                            onClick={() => setVerificationValue('')}
                            aria-label="Clear verification key"
                            title="Clear"
                          >
                            <Icon name="clear" />
                          </button>
                        </span>
                      </header>
                      <textarea
                        className="plain-code-input"
                        value={verificationValue}
                        onChange={(event) => setVerificationValue(event.target.value)}
                        placeholder={
                          asymmetricDecoder
                            ? 'Public Key in SPKI PEM or JWK JSON format.'
                            : 'Enter the HMAC secret.'
                        }
                        spellCheck={false}
                        aria-label="JWT verification key"
                      />
                    </article>
                  )}
                  <Status tone={signatureStatus.tone}>{signatureStatus.message}</Status>
                </section>
              </div>
            </div>
          </section>

          <section
            id="encoder-panel"
            className="mode-panel"
            role="tabpanel"
            aria-labelledby="encoder-tab"
            hidden={mode !== 'encoder'}
          >
            <div className="encoder-grid">
              <div className="encoder-fields">
                <section className="encoder-section">
                  <h2>Header</h2>
                  <article className="editor-card json-editor-card">
                    <header className="card-header">
                      <span className="card-title">
                        <span className="terminal-icon">›_</span> Algorithm &amp; Token Type
                      </span>
                      <button
                        className="icon-button"
                        onClick={() => setHeaderText(DEFAULT_HEADER)}
                        aria-label="Reset header"
                        title="Reset"
                      >
                        <Icon name="clear" />
                      </button>
                    </header>
                    <HighlightedTextarea
                      value={headerText}
                      onChange={setHeaderText}
                      markup={jsonMarkup(headerText)}
                      className="json-code-input"
                      ariaLabel="JWT header editor"
                    />
                  </article>
                  <Status tone={parsedHeader.error ? 'error' : 'success'}>
                    {parsedHeader.error ?? 'Valid header'}
                  </Status>
                </section>

                <section className="encoder-section">
                  <h2>Payload</h2>
                  <article className="editor-card json-editor-card payload-editor-card">
                    <header className="card-header">
                      <span className="card-title">
                        <span className="terminal-icon">›_</span> Data
                      </span>
                      <button
                        className="icon-button"
                        onClick={() => setPayloadText(DEFAULT_PAYLOAD)}
                        aria-label="Reset payload"
                        title="Reset"
                      >
                        <Icon name="clear" />
                      </button>
                    </header>
                    <HighlightedTextarea
                      value={payloadText}
                      onChange={setPayloadText}
                      markup={jsonMarkup(payloadText)}
                      className="json-code-input payload-code-input"
                      ariaLabel="JWT payload editor"
                    />
                  </article>
                  <Status tone={parsedPayload.error ? 'error' : 'success'}>
                    {parsedPayload.error ?? 'Valid payload'}
                  </Status>
                </section>

                <section className="encoder-section signing-section">
                  <div className="section-title-row verification-title">
                    <div>
                      <h2>
                        {encoderKeyKind === 'none'
                          ? 'Unsecured JWT'
                          : `Sign JWT: ${asymmetricEncoder ? 'Private Key' : 'Secret'}`}
                      </h2>
                      <p>
                        {encoderKeyKind === 'none'
                          ? 'The selected algorithm creates an empty signature.'
                          : asymmetricEncoder
                            ? 'Enter the private key used to sign this JWT.'
                            : 'Enter a secret to sign this JWT.'}
                      </p>
                    </div>
                    {encoderKeyKind === 'secret' && (
                      <label className="switch-label">
                        <span>Base64url encoded</span>
                        <input
                          type="checkbox"
                          checked={signingEncoded}
                          onChange={(event) => setSigningEncoded(event.target.checked)}
                        />
                        <span className="switch" aria-hidden="true" />
                      </label>
                    )}
                  </div>
                  {encoderKeyKind !== 'none' && (
                    <article className="editor-card verification-card">
                      <header className="card-header">
                        <span className="card-title">
                          <span className="terminal-icon">›_</span>{' '}
                          {asymmetricEncoder ? 'Private Key' : 'Secret'}
                        </span>
                        <CopyButton
                          label="Copy signing key"
                          onClick={() => void copy(signingValue, 'Key copied')}
                        />
                      </header>
                      <textarea
                        className="plain-code-input"
                        value={signingValue}
                        onChange={(event) => setSigningValue(event.target.value)}
                        placeholder={
                          asymmetricEncoder
                            ? 'Private Key in PKCS #8 PEM or JWK JSON format. The key never leaves your browser.'
                            : 'Enter the HMAC secret.'
                        }
                        spellCheck={false}
                        aria-label="JWT signing key"
                      />
                    </article>
                  )}
                  <Status tone={encodingError ? 'error' : 'success'}>
                    {encodingError || (encoderKeyKind === 'none' ? 'No key required' : 'Valid signing key')}
                  </Status>
                </section>
              </div>

              <div className="encoded-output-column">
                <h2>JWT Signature</h2>
                <article className="editor-card token-card encoded-output-card">
                  <header className="card-header">
                    <span className="card-title">
                      <span className="terminal-icon">›_</span> Encoded JWT
                    </span>
                    <CopyButton
                      label="Copy encoded JWT"
                      onClick={() => void copy(encodedOutput, 'Token copied')}
                    />
                  </header>
                  <div className="encoded-output-wrap">
                    <pre
                      tabIndex={0}
                      aria-label="Encoded JWT"
                      dangerouslySetInnerHTML={{ __html: tokenPartMarkup(encodedOutput) }}
                    />
                  </div>
                </article>
                <Status tone={encodingError ? 'error' : 'success'}>
                  {encodingError || 'JWT generated locally'}
                </Status>
              </div>
            </div>
          </section>
        </section>
      </main>

      <div className={`toast ${toast ? 'is-visible' : ''}`} role="status" aria-live="polite">
        {toast}
      </div>
    </>
  )
}
